import NewAnalysisForm from '../components/NewAnalysisForm';
import NewAnalysisFormSkeleton from '../components/NewAnalysisFormSkeleton';
import OverviewCards from '../components/internal-seo/OverviewCards';
import { useGetAnalysisOverviewQuery } from '@/app/PageSpeed/api/analysisApi';
import { useParams } from 'react-router-dom';
import SpeedOverview from '../components/SpeedOverview';
import { Card, Col, Row } from 'react-bootstrap';
import Background from '@/components/common/Background';
import corner2 from '@/assets/img/icons/spot-illustrations/corner-2.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '@/components/common/Flex';
import { colorByIndex, urlToArray } from '@/helpers/utils';
import CardPieChart from '@/app/common/components/CardPieChart';
import NoAnalysisData from '../components/NoAnalysisData';
import AnalysisIncompleteState from '../components/AnalysisIncompleteState';
import { isAnalysisFinished } from '../utils/analysisStatus';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import OverviewCardsSkeleton from '../components/OverviewCardsSkeleton';
import SpeedOverviewSkeleton from '../components/SpeedOverviewSkeleton';
import CardSkeleton from '@/app/common/components/skeleton/CardSkeleton';
import type {
  HeaderStats,
  ImageStats,
  LinkStats,
  MetaStats
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { ComponentType } from 'react';

const TypedCardPieChart = CardPieChart as ComponentType<any>;
const TypedCardSkeleton = CardSkeleton as ComponentType<any>;

const Overview = () => {
  const { slug } = useParams<{ slug?: string }>();
  const access = useToolAccess('analysis');
  const {
    data: analysisData,
    isFetching: isLoadingDetails,
    isError,
    error,
    refetch,
    isFetching
  } = useGetAnalysisOverviewQuery(slug || '', {
    skip: access.isLoading || !access.haveAccess || !slug
  });
  const contentBlock = analysisData?.data?.content;
  const {
    title,
    description,
    url,
    canonical,
    robots,
    langauge,
    publisher,
    keywords,
    content
  } = contentBlock || {};
  const stats = analysisData?.data?.statistics;
  const linkStats = (stats?.link_stats || {}) as LinkStats;
  const headerStats = (stats?.header_stats || {}) as HeaderStats;
  const imageStats = (stats?.image_stats || {}) as ImageStats;
  const metaStats = (stats?.meta_stats || {}) as MetaStats;

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار واکاوی" />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoadingDetails && !analysisData?.data) return <NoAnalysisData />;
  if (
    !isLoadingDetails &&
    !isAnalysisFinished(analysisData?.data?.status)
  ) {
    return (
      <AnalysisIncompleteState
        status={analysisData?.data?.status}
        taskId={analysisData?.data?.task_id}
        onRefresh={() => refetch()}
      />
    );
  }

  return (
    <>
      {!isLoadingDetails ? (
        <NewAnalysisForm initialSettings={analysisData?.data?.settings} />
      ) : (
        <NewAnalysisFormSkeleton />
      )}
      <div dir="rtl" id="">
        {!isLoadingDetails && analysisData?.data ? (
          <>
            <SpeedOverview analysisData={analysisData} />
            <OverviewCards
              canonical={canonical}
              description={description}
              keywords={keywords}
              langauge={langauge}
              publisher={publisher}
              url={url}
              robotsFileUrl={robots?.robotsFileUrl}
              robotsTag={robots?.robotsTag}
              title={title}
              estimated_time={content?.estimated_time}
              word_count={content?.word_count}
              sentences_count={content?.sentences_count}
            />
            <Card className="h-100 my-3 overflow-hidden">
              <Background image={corner2} className="rounded-soft bg-card" />
              <Card.Body className="z-999">
                <Row className="position-relative g-4">
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex justify-content-end justify-content-md-start flex-wrap gap-3"
                  >
                    <div className="icon-item icon-item-md border rounded-3 shadow-none me-2">
                      <FontAwesomeIcon
                        size="lg"
                        icon="align-right"
                        className={`text-primary`}
                      />
                    </div>
                    <div className="d-flex ms-3 gap-3">
                      {Object.entries(
                        analysisData?.data?.statistics?.header_stats || {}
                      ).map(([key, value]) => (
                        <Flex
                          key={key}
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <h6 className="mb-0 fw-bold text-700 ">
                            {key === 'all' ? 'همه' : key.toUpperCase()}
                          </h6>
                          <p className={`mb-0 fs-0 text-900 fw-medium`}>
                            {(value as number) || 0}
                          </p>
                        </Flex>
                      ))}
                    </div>

                    <div className="d-flex gap-3 ms-5 justify-content-start">
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">تصاویر</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          {analysisData?.data?.statistics?.image_stats?.alt
                            ?.total || 0}
                        </p>
                      </Flex>
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">پیوند</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          {analysisData?.data?.statistics?.link_stats?.protocol
                            ?.total || 0}
                        </p>
                      </Flex>
                    </div>
                  </Col>
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <div className="d-flex fw-bold gap-3 ms-5 justify-content-start align-items-center">
                      {url ? (
                        <a
                          target="_blank"
                          rel="noreferrer noopener"
                          href={`${urlToArray(
                            new URL(url).origin + new URL(url).pathname
                          )
                            .slice(0, 2)
                            .join('')}/sitemap.xml`}
                        >
                          Sitemap.xml
                        </a>
                      ) : (
                        <span>Sitemap.xml</span>
                      )}{' '}
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        href={robots?.robotsFileUrl}
                      >
                        Robots.txt
                      </a>{' '}
                    </div>
                  </Col>
                </Row>{' '}
              </Card.Body>
            </Card>
            <div className="overflow-hidden">
              <Row className="g-3 mb-3">
                <Col md={6} xl={4}>
                  <TypedCardPieChart
                    title="توزیع تعداد پیوند"
                    tooltip={''}
                    data={[
                      {
                        name: 'یکتا',
                        value: linkStats.count_links?.['1'],
                        color: colorByIndex(1)
                      },
                      {
                        name: '۲ بار تکرار',
                        value: linkStats.count_links?.['2'],
                        color: colorByIndex(2)
                      },
                      {
                        name: '۳ بار تکرار',
                        value: linkStats.count_links?.['3'],
                        color: colorByIndex(3)
                      },
                      {
                        name: '۴ بار تکرار',
                        value: linkStats.count_links?.['4'],
                        color: colorByIndex(4)
                      },
                      {
                        name: '۵ بار تکرار',
                        value: linkStats.count_links?.['5'],
                        color: colorByIndex(5)
                      },
                      {
                        name: 'بالای ۵ بار تکرار',
                        value: linkStats.count_links?.more,
                        color: colorByIndex(6)
                      }
                    ]}
                    radius={['100%', '77%']}
                  />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardPieChart
                    title="توزیع پروتکل"
                    tooltip={''}
                    data={[
                      {
                        name: 'https',
                        value: linkStats.protocol?.https,
                        color: 'success'
                      },
                      {
                        name: 'http',
                        value: linkStats.protocol?.http,
                        color: 'danger'
                      }
                    ]}
                    radius={['100%', '77%']}
                  />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardPieChart
                    title="توزیع پیوند فالو / نوفالو"
                    tooltip={''}
                    data={[
                      {
                        name: 'فالو',
                        value: linkStats.rel?.follow,
                        color: 'success'
                      },
                      {
                        name: 'نوفالو',
                        value: linkStats.rel?.nofollow,
                        color: 'warning'
                      }
                    ]}
                    radius={['100%', '77%']}
                  />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardPieChart
                    title="توزیع هدینگ"
                    tooltip={''}
                    data={['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(
                      (tag, i) => ({
                        name: tag,
                        value: headerStats[tag],
                        color: colorByIndex(i)
                      })
                    )}
                    radius={['100%', '77%']}
                  />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardPieChart
                    title="توزیع متا تگ"
                    tooltip={''}
                    data={['og', 'twitter', 'link', 'other'].map((mode, i) => ({
                      name: mode,
                      value: metaStats[mode],
                      color: colorByIndex(i)
                    }))}
                    radius={['100%', '77%']}
                  />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardPieChart
                    title="تصاویر با / بدون متن‌جایگزین"
                    tooltip={''}
                    data={[
                      {
                        name: 'دارای متن‌جایگزین',
                        value: imageStats.alt?.with_alt,
                        color: 'success'
                      },
                      {
                        name: 'بدون متن‌جایگزین',
                        value: imageStats.alt?.without_alt,
                        color: 'danger'
                      }
                    ]}
                    radius={['100%', '77%']}
                  />
                </Col>
              </Row>
            </div>
          </>
        ) : (
          <>
            <SpeedOverviewSkeleton />
            <OverviewCardsSkeleton />
            <Card className="h-100 my-3 overflow-hidden">
              <Background image={corner2} className="rounded-soft bg-card" />
              <Card.Body className="z-999">
                <Row className="position-relative g-4">
                  <Col xs={12} lg={6} className="d-flex flex-wrap gap-3">
                    <div className="icon-item icon-item-md border rounded-3 shadow-none me-2">
                      <FontAwesomeIcon
                        size="lg"
                        icon="align-right"
                        className={`text-primary`}
                      />
                    </div>
                    <div className="d-flex ms-3 gap-3">
                      {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(key => (
                        <Flex
                          key={key}
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <h6 className="mb-0 fw-bold text-700 ">
                            {key === 'all' ? 'همه' : key.toUpperCase()}
                          </h6>
                          <p className={`mb-0 fs-0 text-900 fw-medium`}>
                            <TextLoading height={10} isWidthPx width={15} />
                          </p>
                        </Flex>
                      ))}
                    </div>

                    <div className="d-flex gap-3 ms-5 justify-content-start ">
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">تصاویر</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          <TextLoading height={10} isWidthPx width={15} />
                        </p>
                      </Flex>
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">پیوندها</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          <TextLoading height={10} isWidthPx width={15} />
                        </p>
                      </Flex>
                    </div>
                  </Col>
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <div className="d-flex fw-bold gap-3 ms-5 justify-content-start align-items-center">
                      <span>Sitemap.xml</span>
                      <span> Robots.txt</span>
                    </div>
                  </Col>
                </Row>{' '}
              </Card.Body>
            </Card>
            <div className="overflow-hidden">
              <Row className="g-3 mb-3">
                <Col md={6} xl={4}>
                  <TypedCardSkeleton />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardSkeleton />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardSkeleton />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardSkeleton />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardSkeleton />
                </Col>
                <Col md={6} xl={4}>
                  <TypedCardSkeleton />
                </Col>
              </Row>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Overview;
